import { useEffect, useState } from 'react';
import Icon from '../components/Icon';
import PageHeader from '../components/PageHeader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ApiError } from '../lib/api';
import { disconnectAccount } from '../lib/sections-api';
import {
  INSTAGRAM_START_PATH,
  listInstagramAccountsRequest,
  type InstagramAccount,
} from '../lib/instagram-api';

type LoadState = 'loading' | 'ready' | 'error';

function accountTypeLabel(accountType: string | null): string {
  if (accountType === 'Business') {
    return 'حساب تجاری';
  }
  if (accountType === 'Media_Creator') {
    return 'کریتور مدیا';
  }
  return 'نوع حساب نامشخص';
}

function statusBadge(status: string): { label: string; className: string } {
  if (status === 'connected') {
    return { label: 'متصل', className: 'bg-emerald-50 text-emerald-700' };
  }
  if (status === 'disconnected') {
    return { label: 'قطع شده', className: 'bg-amber-50 text-amber-800' };
  }
  if (status === 'error') {
    return { label: 'خطا در اتصال', className: 'bg-rose-50 text-rose-700' };
  }
  return { label: 'نامشخص', className: 'bg-slate-100 text-slate-600' };
}

function webhookBadge(status: string): { dot: string; text: string } {
  if (status === 'subscribed') {
    return { dot: 'bg-emerald-500', text: 'رویدادها فعال' };
  }
  if (status === 'failed') {
    return { dot: 'bg-rose-500', text: 'رویدادها ناموفق' };
  }
  return { dot: 'bg-slate-400', text: 'رویدادها در انتظار' };
}

function formatFollowers(count: number | null): string {
  return count === null ? '—' : count.toLocaleString('fa-IR');
}

function formatDate(timestamp: number | null): string {
  if (timestamp === null) {
    return '—';
  }
  return new Date(timestamp).toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function accountsErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status >= 500) {
    return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
  }
  return 'دریافت فهرست حساب‌ها ناموفق بود. دوباره تلاش کنید.';
}

export default function SettingsPage() {
  useDocumentTitle('تنظیمات');

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [accounts, setAccounts] = useState<InstagramAccount[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listInstagramAccountsRequest()
      .then((response) => {
        if (!cancelled) {
          setAccounts(response.accounts);
          setLoadError(null);
          setLoadState('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(accountsErrorMessage(error));
          setLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function handleRetry(): void {
    setLoadError(null);
    setLoadState('loading');
    setReloadKey((key) => key + 1);
  }

  async function handleDisconnect(id: string): Promise<void> {
    setDisconnectingId(id);
    try {
      await disconnectAccount(id);
      setReloadKey((key) => key + 1);
    } catch {
      // ignore
    } finally {
      setDisconnectingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="تنظیمات"
        description="حساب‌های متصل اینستاگرام و وضعیت اتصال آن‌ها را بررسی کنید"
      />

      <section
        aria-labelledby="accounts-heading"
        className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 id="accounts-heading" className="text-base font-bold text-slate-800">
            حساب‌های اینستاگرام
          </h2>
          <a
            href={INSTAGRAM_START_PATH}
            data-testid="connect-instagram"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-800"
          >
            <Icon name="link" className="h-4 w-4" />
            اتصال اینستاگرام
          </a>
        </div>

        {loadState === 'loading' && (
          <div
            data-testid="settings-loading"
            role="status"
            className="flex flex-col items-center gap-3 py-8"
          >
            <span
              className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600"
              aria-hidden="true"
            />
            <p className="text-sm text-slate-500">در حال دریافت حساب‌ها…</p>
          </div>
        )}

        {loadState === 'error' && (
          <div
            data-testid="settings-error"
            role="alert"
            className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-center"
          >
            <p className="text-sm text-rose-700">{loadError}</p>
            <button
              type="button"
              data-testid="settings-retry"
              onClick={handleRetry}
              className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              تلاش مجدد
            </button>
          </div>
        )}

        {loadState === 'ready' && accounts.length === 0 && (
          <div
            data-testid="empty-state"
            className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center"
          >
            <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
              <Icon name="link" className="h-5 w-5" />
            </span>
            <h3 className="mt-3 text-sm font-bold text-slate-800">هیچ حسابی متصل نیست</h3>
            <p className="mx-auto mt-1.5 max-w-xl text-xs leading-5 text-slate-500">
              برای اتصال، روی دکمهٔ «اتصال اینستاگرام» بالا بزنید. اتصال از طریق OAuth رسمی متا
              انجام می‌شود و در هیچ مرحله‌ای گذرواژهٔ اینستاگرام شما خواسته نمی‌شود.
            </p>
          </div>
        )}

        {loadState === 'ready' && accounts.length > 0 && (
          <ul data-testid="accounts-list" className="mt-4 grid gap-4 sm:grid-cols-2">
            {accounts.map((account) => {
              const badge = statusBadge(account.status);
              const webhook = webhookBadge(account.webhook.status);
              return (
                <li
                  key={account.id}
                  data-testid="account-card"
                  data-status={account.status}
                  className="rounded-lg border border-slate-200 bg-white p-4"
                >
                  <div className="flex items-start gap-3">
                    {account.profile_picture_url !== null && account.profile_picture_url !== '' ? (
                      <img
                        src={account.profile_picture_url}
                        alt=""
                        className="h-11 w-11 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo-50 font-bold text-indigo-400">
                        {account.username.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p dir="ltr" className="text-left text-sm font-bold text-slate-900">
                        @{account.username}
                      </p>
                      {account.name !== null && account.name !== '' && (
                        <p dir="ltr" className="text-left text-xs text-slate-500">
                          {account.name}
                        </p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                        <span
                          data-testid="account-status"
                          data-status={account.status}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                          {accountTypeLabel(account.account_type)}
                        </span>
                        <span
                          data-testid="account-webhook"
                          className="inline-flex items-center gap-1.5 text-slate-500"
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${webhook.dot}`}
                            aria-hidden="true"
                          />
                          {webhook.text}
                        </span>
                      </div>
                    </div>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-3 text-xs">
                    <div>
                      <dt className="text-slate-400">شناسه اینستاگرام</dt>
                      <dd dir="ltr" className="text-left font-semibold text-slate-700">
                        {account.ig_user_id}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">دنبال‌کننده</dt>
                      <dd className="font-semibold text-slate-700">
                        {formatFollowers(account.followers_count)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">تاریخ اتصال</dt>
                      <dd className="font-semibold text-slate-700">
                        {formatDate(account.connected_at)}
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    <button
                      type="button"
                      data-testid={`disconnect-${account.id}`}
                      onClick={() => void handleDisconnect(account.id)}
                      disabled={disconnectingId !== null || account.status !== 'connected'}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Icon name="logout" className="h-3.5 w-3.5" />
                      {disconnectingId === account.id ? 'در حال قطع…' : 'قطع اتصال'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
